import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Avatar } from "@material-ui/core";
import { AiOutlineClose } from "react-icons/ai";
import "./style.scss";
import { getDirectUsers } from "services/NexiDigitalAPI";

interface Direct {
  id: string;
  name: string;
  position: string;
  avatar?: string;
  status: string;
}

interface DirectsModalProps {
  show: boolean;
  onHide: () => void;
  trainingTitle: string;
  classTitle: string;
  directsCount: number;
  idTeam: number;
}

const statusTranslate = (currentStatusTranslate: string) => {
  const arrStatus = [
    "NOT_ATTEMPTED",
    "IN_PROGRESS",
    "COMPLETED",
    "PASSED",
    "FAILED",
    "EXPIRED",
    "EQUIVALENCY",
  ];
  const arrStatusTranslate = [
    "Não iniciado",
    "Em progresso",
    "Concluído",
    "Aprovado",
    "Reprovado",
    "Expirado",
    "Concluído por equivalência",
  ];
  const translateWordIndex = arrStatus.indexOf(currentStatusTranslate);
  let translatedStatus = "-";

  if (translateWordIndex !== -1) {
    translatedStatus = arrStatusTranslate[translateWordIndex];
  }

  return translatedStatus;
};

const StatusBadge = React.memo(({ status }: { status: string }) => {
  const getStatusClass = (status: string) => {
    switch (status) {
      case "Aprovado":
      case "COMPLETED":
      case "PASSED":
        return "approved";
      case "Reprovado":
      case "NOT_ATTEMPTED":
      case "FAILED":
      case "EXPIRED":
        return "rejected";
      case "Em progresso":
      case "IN_PROGRESS":
        return "in-progress";
      case "Concluído":
        return "completed";
      case "Não iniciado":
        return "not-started";
      case "Expirado":
        return "expired";
      case "Concluído por equivalência":
      case "EQUIVALENCY":
        return "equivalency";
      default:
        return "default";
    }
  };

  const statusClass = getStatusClass(status);

  return (
    <span className={`status-badge ${statusClass}`}>
      {statusTranslate(status)}
    </span>
  );
});

const DirectsModal: React.FC<DirectsModalProps> = ({
  show,
  onHide,
  trainingTitle,
  classTitle,
  directsCount,
  idTeam,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [directs, setDirects] = useState<Direct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRows, setTotalRows] = useState(0);

  const fetchDirects = useCallback(async (page = currentPage, perPage = rowsPerPage) => {
    setLoading(true);
    setError(null);

    try {
      const {
        data: { result, totalRecords },
      } = await getDirectUsers(idTeam, {
        offset: (page - 1) * perPage,
        limit: perPage,
        shouldIncludeFormerWorkTeamMembersWithPendingEvaluations: true,
      });
      const apiDirects =
        result.map((user: any) => ({
          id: user.idUser,
          name: user.name || "Nome não disponível",
          position: user.role || "Cargo não informado",
          avatar: user.photo || "",
          status: user.status,
        })) || [];

      setDirects(apiDirects);
      setTotalRows(totalRecords ?? apiDirects.length);
    } catch (err) {
      console.error("Erro ao buscar dados das pessoas avaliadas:", err);
      setError("Erro ao carregar dados das pessoas avaliadas");
    } finally {
      setLoading(false);
    }
  }, [idTeam, currentPage, rowsPerPage]);

  useEffect(() => {
    if (show && idTeam) {
      setCurrentPage(1);
      setRowsPerPage(10);
    }
  }, [show, idTeam, directsCount]);

  useEffect(() => {
    if (show && idTeam) {
      fetchDirects();
    }
  }, [show, idTeam, currentPage, rowsPerPage, fetchDirects]);

  const paginationComponentOptions = useMemo(() => ({
    rowsPerPageText: "Pessoas por página:",
    rangeSeparatorText: "de",
    selectAllRowsItem: false,
    selectAllRowsItemText: "Todos",
  }), []);

  const columns = useMemo(() => [
    {
      name: "Nome",
      selector: "name",
      sortable: false,
      left: true,
      grow: 2,
      cell: (row: Direct) => (
        <div className="name-cell">
          <Avatar src={row.avatar} alt={row.name} className="direct-avatar">
            {row.name.charAt(0)}
          </Avatar>
          <div className="name-info">
            <div className="name-text">{row.name}</div>
            {row.position && <div className="position-text">{row.position}</div>}
          </div>
        </div>
      ),
    },
    {
      name: "Status",
      selector: "status",
      sortable: false,
      center: true,
      width: "150px",
      cell: (row: Direct) => <StatusBadge status={row.status} />,
    },
  ], []);

  const customTableStyles = useMemo(() => ({
    headCells: {
      style: {
        backgroundColor: "#6b48ff",
        color: "white",
        paddingLeft: "30px",
        paddingRight: "30px",
        fontWeight: "bold",
        fontSize: "14px",
      },
    },
    cells: {
      style: {
        paddingLeft: "30px",
        paddingRight: "30px",
        paddingTop: "12px",
        paddingBottom: "12px",
      },
    },
    rows: {
      style: {
        minHeight: "60px",
      },
    },
  }), []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number, page: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(page);
  }, []);

  const handleRetry = useCallback(() => {
    fetchDirects();
  }, [fetchDirects]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      keyboard={false}
      aria-labelledby="directs-modal-title"
      centered
      size="lg"
      className="directs-modal"
    >
      <div className="modal-support-blur" />

      <div className="modal-content directs-modal-content">
        <Modal.Header className="directs-modal-header">
          <Modal.Title id="directs-modal-title">Lista de pessoas nesta avaliação</Modal.Title>
          <button type="button" className="close-button" onClick={onHide} aria-label="Fechar modal">
            <AiOutlineClose />
          </button>
        </Modal.Header>

        <Modal.Body className="directs-modal-body">
          <div className="context-info">
            <div className="info-row">
              <span className="info-label">Treinamento:</span>
              <span className="info-link">{trainingTitle}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Turma:</span>
              <span className="info-link">{classTitle}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Pessoas avaliadas:</span>
              <span className="info-link">{totalRows}</span>
            </div>
            <div className="info-row">
              <p>Algumas avaliações podem ter sido criadas quando o colaborador ainda era da sua equipe. Você ainda precisa respondê-las.</p>
            </div>

          </div>

          {loading && (
            <div className="loading-container">
              <div className="loading-message">Carregando...</div>
            </div>
          )}

          {error && (
            <div className="error-container">
              <div className="error-message">
                <p>{error}</p>
                <button className="retry-button" onClick={handleRetry}>
                  Tentar novamente
                </button>
              </div>
            </div>
          )}

          {!loading && !error && (
            <div className="directs-table-container">
              <DataTable
                columns={columns}
                data={directs}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationDefaultPage={currentPage}
                paginationPerPage={rowsPerPage}
                paginationRowsPerPageOptions={[10, 15, 20, 25]}
                paginationComponentOptions={paginationComponentOptions}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handleRowsPerPageChange}
                customStyles={customTableStyles}
                noHeader={false}
                fixedHeader={true}
                fixedHeaderScrollHeight="400px"
                highlightOnHover
                responsive
                noDataComponent={<div className="no-data-message">Nenhuma pessoa encontrado</div>}
                paginationResetDefaultPage={currentPage === 1}
              />
            </div>
          )}
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default DirectsModal;
